import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQCode from '../../components/code';
import UQSpacer from '../../components/spacer';
import UQNavbar from '../../components/navbar';
import UQIcon from '../../components/icon';
import UQButton from '../../components/button';
import { BREADCRUMB_ROOT } from '../../common/config';
import { Link } from 'react-router-dom';

// Import UNQ logos
import uniqLightLogo from '../../assets/images/logos/uniq_light.svg?url';
import uniqDarkLogo from '../../assets/images/logos/uniq_dark.svg?url';
import logoLight from '../../assets/images/logos/logo_light.svg?url';
import logoDark from '../../assets/images/logos/logo_dark.svg?url';
import logoLightSmall from '../../assets/images/logos/logo_light_small.png?url';
import logoDarkSmall from '../../assets/images/logos/logo_dark_small.svg?url';

// Responsive UNQ Navbar Example Component
const ResponsiveUNQNavbarExample = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigationLinks = [
    { text: 'Dashboard', url: '/', icon: 'home-01' },
    { 
      text: 'Projects', 
      url: '/projects', 
      icon: 'folder',
      subLevel: [
        { text: 'Active Projects', url: '/projects/active', icon: 'activity' },
        { text: 'Completed Projects', url: '/projects/completed', icon: 'check-circle' },
        { text: 'Archived Projects', url: '/projects/archived', icon: 'archive' },
        { 
          text: 'Templates', 
          url: '/projects/templates', 
          icon: 'template',
          subLevel: [
            { text: 'Web Templates', url: '/projects/templates/web', icon: 'globe' },
            { text: 'Mobile Templates', url: '/projects/templates/mobile', icon: 'smartphone' },
            { text: 'API Templates', url: '/projects/templates/api', icon: 'code' }
          ]
        }
      ]
    },
    {
      text: 'Analytics',
      url: '/analytics',
      icon: 'activity',
      subLevel: [
        { text: 'Performance', url: '/analytics/performance', icon: 'trending-up' },
        { text: 'User Insights', url: '/analytics/users', icon: 'users' },
        { text: 'Revenue Reports', url: '/analytics/revenue', icon: 'dollar-sign' },
        {
          text: 'Custom Reports',
          url: '/analytics/custom',
          icon: 'bar-chart',
          subLevel: [
            { text: 'Create Report', url: '/analytics/custom/create', icon: 'plus' },
            { text: 'Saved Reports', url: '/analytics/custom/saved', icon: 'bookmark' },
            { text: 'Scheduled Reports', url: '/analytics/custom/scheduled', icon: 'clock' }
          ]
        }
      ]
    },
    {
      text: 'Team',
      url: '/team',
      icon: 'users',
      subLevel: [
        { text: 'Members', url: '/team/members', icon: 'user' },
        { text: 'Roles & Permissions', url: '/team/roles', icon: 'shield' },
        { text: 'Departments', url: '/team/departments', icon: 'briefcase' },
        {
          text: 'HR Management',
          url: '/team/hr',
          icon: 'user-plus',
          subLevel: [
            { text: 'Onboarding', url: '/team/hr/onboarding', icon: 'user-check' },
            { text: 'Performance Reviews', url: '/team/hr/reviews', icon: 'star' },
            { text: 'Time Tracking', url: '/team/hr/time', icon: 'clock' }
          ]
        }
      ]
    },
    {
      text: 'Settings',
      url: '/settings',
      icon: 'settings',
      subLevel: [
        { text: 'General', url: '/settings/general', icon: 'sliders' },
        { text: 'Security', url: '/settings/security', icon: 'lock' },
        { text: 'Notifications', url: '/settings/notifications', icon: 'bell' },
        { text: 'Billing', url: '/settings/billing', icon: 'credit-card' },
        {
          text: 'Integrations',
          url: '/settings/integrations',
          icon: 'link',
          subLevel: [
            { text: 'API Keys', url: '/settings/integrations/api', icon: 'key' },
            { text: 'Webhooks', url: '/settings/integrations/webhooks', icon: 'zap' },
            { text: 'Third-party Apps', url: '/settings/integrations/apps', icon: 'grid' }
          ]
        }
      ]
    },
    { text: 'Help', url: '/help', icon: 'help-circle' }
  ];

  return (
    <div style={{ border: '1px solid #ccc', padding: '1rem' }}>
      <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--info-bg, #e3f2fd)', borderRadius: '4px', fontSize: '0.875rem' }}>
        <strong>Current Mode:</strong> {isMobile ? 'Mobile (Horizontally Collapsed)' : 'Desktop (Normal)'} - Resize window to see the responsive behavior
      </div>
      <UQNavbar
        links={navigationLinks}
        logoLight={logoLight}
        logoDark={logoDark}
        logoLightSmall={logoLightSmall}
        logoDarkSmall={logoDarkSmall}
        horizontalCollapsed={isMobile}
        location={window.location}
        renderLink={(link, children) => (
          <Link to={link.url} className="navbar-item-base">
            {children}
          </Link>
        )}
        content={
          <div style={{ flex: 1, padding: '2rem', background: 'var(--bg-color, #f8f9fa)', color: 'var(--text-color, #212529)' }}>
            <h3>UNQ Consulting Platform - Responsive Design</h3>
            <p>This responsive navbar automatically adapts to your screen size. On mobile (&lt;768px), it uses the space-efficient horizontally collapsed layout to maximize content space on small screens. On desktop (≥768px), it expands to the full normal layout for complete navigation visibility.</p>
            <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--card-bg, #ffffff)', borderRadius: '8px', border: '1px solid var(--border-color, #e9ecef)' }}>
              <h4>Responsive Features:</h4>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                <li><strong>Mobile:</strong> Horizontal collapsed layout for space efficiency</li>
                <li><strong>Desktop:</strong> Full navbar layout for complete navigation visibility</li>
                <li><strong>Automatic:</strong> Switches based on window width (768px breakpoint)</li>
                <li><strong>Multi-level:</strong> Dropdown navigation works in both modes</li>
                <li><strong>Branded:</strong> Real UNQ logos adapt to each layout</li>
                <li><strong>Smooth:</strong> Real-time responsive behavior</li>
              </ul>
            </div>
          </div>
        }
      />
    </div>
  );
};

// Toggle Collapse Example Component
const ToggleCollapseExample = () => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const svgLogoString = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="20" height="20" fill="currentColor">
      <circle cx="20" cy="20" r="20" />
      <text x="40%" y="45%" text-anchor="middle" fill="#fff" font-size="7">Your Logo</text>
    </svg>
  `;

  const svgSmallLogoString = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="16" height="16" fill="currentColor">
      <circle cx="20" cy="20" r="18" />
      <text x="50%" y="55%" text-anchor="middle" fill="#fff" font-size="12" font-weight="bold">L</text>
    </svg>
  `;

  const svgLogoBase64 = `data:image/svg+xml;base64,${btoa(svgLogoString)}`;
  const svgSmallLogoBase64 = `data:image/svg+xml;base64,${btoa(svgSmallLogoString)}`;

  const toggleCollapseCode = `
import React from 'react';
import UQNavbar from '@uniqconsulting/ui';
import UQButton from '@uniqconsulting/ui';

const ToggleNavbarExample = () => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const links = [
    { text: 'Dashboard', url: '/dashboard', icon: 'home-01' },
    { text: 'Analytics', url: '/analytics', icon: 'activity' },
    { text: 'Settings', url: '/settings', icon: 'settings' },
    { text: 'Profile', url: '/profile', icon: 'user' },
  ];

  return (
    <div>
      <UQButton 
        onClick={() => setIsCollapsed(!isCollapsed)}
        type="secondary"
        size="small"
        style={{ marginBottom: '1rem' }}
      >
        {isCollapsed ? 'Expand Navbar' : 'Collapse Navbar'}
      </UQButton>
      
      <UQNavbar
        links={links}
        logoLight="/path/to/logo_light.svg"
        logoDark="/path/to/logo_dark.svg"
        logoLightSmall="/path/to/small_logo_light.svg"
        logoDarkSmall="/path/to/small_logo_dark.svg"
        horizontalCollapsed={isCollapsed}
        location={window.location}
        renderLink={(link, children) => (
          <Link to={link.url} className="navbar-item-base">
            {children}
          </Link>
        )}
        content={
          <div style={{ padding: '1rem' }}>
            <h3>Content Area</h3>
            <p>Toggle the navbar to see how the content area adjusts.</p>
          </div>
        }
      />
    </div>
  );
};
`;

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <UQButton 
          onClick={() => setIsCollapsed(!isCollapsed)}
          type="secondary"
          size="small"
        >
          <UQIcon icon={isCollapsed ? "arrow-expand-01" : "arrow-collapse-01"} />
          {isCollapsed ? 'Expand Navbar' : 'Collapse Navbar'}
        </UQButton>
      </div>
      
      <div style={{ border: '1px solid #ccc', padding: '1rem', minHeight: '400px' }}>
        <UQNavbar
          links={[
            { text: 'Dashboard', url: '/dashboard', icon: 'home-01' },
            { text: 'Analytics', url: '/analytics', icon: 'activity' },
            { text: 'Settings', url: '/settings', icon: 'settings' },
            { text: 'Profile', url: '/profile', icon: 'user' },
          ]}
          logoLight={svgLogoBase64}
          logoDark={svgLogoBase64}
          logoLightSmall={svgSmallLogoBase64}
          logoDarkSmall={svgSmallLogoBase64}
          horizontalCollapsed={isCollapsed}
          location={window.location}
          renderLink={(link, children) => (
            <Link to={link.url} className="navbar-item-base">
              {children}
            </Link>
          )}
          content={
            <div style={{ flex: 1, padding: '1rem', background: 'var(--bg-color, #f8f9fa)', color: 'var(--text-color, #212529)' }}>
              <h3>Content Area</h3>
              <p>
                Current navbar state: <strong>{isCollapsed ? 'Collapsed' : 'Normal'}</strong>
              </p>
              <p>Toggle the navbar using the button above to see how the layout dynamically adjusts. In collapsed mode, the navbar takes minimal space and shows only icons with tooltips.</p>
              {isCollapsed && (
                <p><em>Notice how the content area expands when the navbar is collapsed, giving you more space for your main content.</em></p>
              )}
            </div>
          }
        />
      </div>
      <UQSpacer size="s" />
      <UQCode code={toggleCollapseCode} enableCopy />
    </div>
  );
};

const NavbarPage = () => {
  const svgLogoString = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="20" height="20" fill="currentColor">
      <circle cx="20" cy="20" r="20" />
      <text x="40%" y="45%" text-anchor="middle" fill="#fff" font-size="7">Your Logo</text>
    </svg>
  `;

  const svgSmallLogoString = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="16" height="16" fill="currentColor">
      <circle cx="20" cy="20" r="18" />
      <text x="50%" y="55%" text-anchor="middle" fill="#fff" font-size="12" font-weight="bold">L</text>
    </svg>
  `;

  const svgLogoBase64 = `data:image/svg+xml;base64,${btoa(svgLogoString)}`;
  const svgSmallLogoBase64 = `data:image/svg+xml;base64,${btoa(svgSmallLogoString)}`;

  const basicNavbarCode = `
import UQNavbar from '@uniqconsulting/ui';

const links = [
  { text: 'Home', url: '/', icon: 'home-01' },
  { text: 'About', url: '/about', icon: 'info' },
  { text: 'Contact', url: '/contact', icon: 'mail' },
];

<UQNavbar
  links={links}
  logoLight="your_logo_light_url"
  logoDark="your_logo_light_url"
  location={window.location}
  renderLink={(link, children) => (
    <Link to={link.url} className="navbar-item-base">
      {children}
    </Link>
  )}
/>;
`;

  const collapsibleNavbarCode = `
import UQNavbar from '@uniqconsulting/ui';

const links = [
  {
    text: 'Services',
    url: '/services',
    icon: 'settings',
    subLevel: [
      { text: 'Consulting', url: '/services/consulting' },
      { text: 'Development', url: '/services/development' },
    ],
  },
  { text: 'Portfolio', url: '/portfolio', icon: 'folder' },
];

<UQNavbar
  links={links}
  logoLight="/path/to/logo_light.svg"
  logoDark="/path/to/logo_dark.svg"
  singleCollapse={true}
  location={window.location}
  renderLink={(link, children) => (
    <Link to={link.url} className="navbar-item-base">
      {children}
    </Link>
  )}
/>;
`;

  const miniNavbarCode = `
import UQNavbar from '@uniqconsulting/ui';

const links = [
  { text: 'Dashboard', url: '/dashboard', icon: 'home-01' },
  { text: 'Reports', url: '/reports', icon: 'activity' },
];

<div style={{ width: '300px', border: '1px solid #ccc', padding: '1rem' }}>
  <UQNavbar
    links={links}
    logoLight="/path/to/logo_light.svg"
    logoDark="/path/to/logo_dark.svg"
    location={window.location}
    renderLink={(link, children) => (
      <Link to={link.url} className="navbar-item-base">
        {children}
      </Link>
    )}
  />
</div>;
`;

  const horizontalCollapsedNavbarCode = `
import UQNavbar from '@uniqconsulting/ui';

const links = [
  { text: 'Dashboard', url: '/dashboard', icon: 'home-01' },
            { text: 'Analytics', url: '/analytics', icon: 'activity' },
  { text: 'Settings', url: '/settings', icon: 'settings' },
  { text: 'Profile', url: '/profile', icon: 'user' },
];

<UQNavbar
  links={links}
  logoLight="/path/to/logo_light.svg"
  logoDark="/path/to/logo_dark.svg"
  logoLightSmall="/path/to/small_logo_light.svg"
  logoDarkSmall="/path/to/small_logo_dark.svg"
  horizontalCollapsed={true}
  location={window.location}
  renderLink={(link, children) => (
    <Link to={link.url} className="navbar-item-base">
      {children}
    </Link>
  )}
/>;
`;

  const smallLogosNavbarCode = `
import UQNavbar from '@uniqconsulting/ui';

const links = [
  { text: 'Home', url: '/', icon: 'home-01' },
  { text: 'About', url: '/about', icon: 'info' },
];

<UQNavbar
  links={links}
  logoLight="/path/to/main_logo_light.svg"
  logoDark="/path/to/main_logo_dark.svg"
  logoLightSmall="/path/to/small_logo_light.svg"
  logoDarkSmall="/path/to/small_logo_dark.svg"
  location={window.location}
  renderLink={(link, children) => (
    <Link to={link.url} className="navbar-item-base">
      {children}
    </Link>
  )}
/>;
`;

  const unqNavbarCode = `
import UQNavbar from '@uniqconsulting/ui';
import logoLight from '../../assets/images/logos/logo_light.svg?url';
import logoDark from '../../assets/images/logos/logo_dark.svg?url';
import logoLightSmall from '../../assets/images/logos/logo_light_small.svg?url';
import logoDarkSmall from '../../assets/images/logos/logo_dark_small.svg?url';

const links = [
  { text: 'Dashboard', url: '/', icon: 'home-01' },
  { 
    text: 'Projects', 
    url: '/projects', 
    icon: 'folder',
    subLevel: [
      { text: 'Active Projects', url: '/projects/active', icon: 'activity' },
      { text: 'Completed Projects', url: '/projects/completed', icon: 'check-circle' },
      { text: 'Archived Projects', url: '/projects/archived', icon: 'archive' },
      { 
        text: 'Templates', 
        url: '/projects/templates', 
        icon: 'template',
        subLevel: [
          { text: 'Web Templates', url: '/projects/templates/web', icon: 'globe' },
          { text: 'Mobile Templates', url: '/projects/templates/mobile', icon: 'smartphone' },
          { text: 'API Templates', url: '/projects/templates/api', icon: 'code' }
        ]
      }
    ]
  },
  {
    text: 'Analytics',
    url: '/analytics',
    icon: 'activity',
    subLevel: [
      { text: 'Performance', url: '/analytics/performance', icon: 'trending-up' },
      { text: 'User Insights', url: '/analytics/users', icon: 'users' },
      { text: 'Revenue Reports', url: '/analytics/revenue', icon: 'dollar-sign' },
      {
        text: 'Custom Reports',
        url: '/analytics/custom',
        icon: 'bar-chart',
        subLevel: [
          { text: 'Create Report', url: '/analytics/custom/create', icon: 'plus' },
          { text: 'Saved Reports', url: '/analytics/custom/saved', icon: 'bookmark' },
          { text: 'Scheduled Reports', url: '/analytics/custom/scheduled', icon: 'clock' }
        ]
      }
    ]
  },
  {
    text: 'Settings',
    url: '/settings',
    icon: 'settings',
    subLevel: [
      { text: 'General', url: '/settings/general', icon: 'sliders' },
      { text: 'Security', url: '/settings/security', icon: 'lock' },
      { text: 'Notifications', url: '/settings/notifications', icon: 'bell' },
      {
        text: 'Integrations',
        url: '/settings/integrations',
        icon: 'link',
        subLevel: [
          { text: 'API Keys', url: '/settings/integrations/api', icon: 'key' },
          { text: 'Webhooks', url: '/settings/integrations/webhooks', icon: 'zap' },
          { text: 'Third-party Apps', url: '/settings/integrations/apps', icon: 'grid' }
        ]
      }
    ]
  },
  { text: 'Help', url: '/help', icon: 'help-circle' }
];

<UQNavbar
  links={links}
  logoLight={logoLight}
  logoDark={logoDark}
  logoLightSmall={logoLightSmall}
  logoDarkSmall={logoDarkSmall}
  horizontalCollapsed={true}
  location={window.location}
  renderLink={(link, children) => (
    <Link to={link.url} className="navbar-item-base">
      {children}
    </Link>
  )}
  content={
    <div style={{ flex: 1, padding: '2rem' }}>
      <h3>UNQ Consulting Platform</h3>
      <p>Welcome to the UNQ Consulting platform.</p>
    </div>
  }
/>;
`;

  const responsiveUNQNavbarCode = `
import React from 'react';
import UQNavbar from '@uniqconsulting/ui';
import logoLight from '../../assets/images/logos/logo_light.svg?url';
import logoDark from '../../assets/images/logos/logo_dark.svg?url';
import logoLightSmall from '../../assets/images/logos/logo_light_small.svg?url';
import logoDarkSmall from '../../assets/images/logos/logo_dark_small.svg?url';

const ResponsiveUNQNavbar = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigationLinks = [
    { text: 'Dashboard', url: '/', icon: 'home-01' },
    { 
      text: 'Projects', 
      url: '/projects', 
      icon: 'folder',
      subLevel: [
        { text: 'Active Projects', url: '/projects/active', icon: 'activity' },
        // ... more navigation items
      ]
    },
    // ... more navigation sections
  ];

  return (
    <UQNavbar
      links={navigationLinks}
      logoLight={logoLight}
      logoDark={logoDark}
      logoLightSmall={logoLightSmall}
      logoDarkSmall={logoDarkSmall}
      horizontalCollapsed={isMobile} // Collapsed on mobile, normal on desktop
      location={window.location}
      renderLink={(link, children) => (
        <Link to={link.url} className="navbar-item-base">
          {children}
        </Link>
      )}
    />
  );
};
`;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Navbar']} />
      <h1>UQNavbar Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQNavbar</code> component is a flexible navigation bar that supports hierarchical links, icons, and a footer section. It also includes features like single-collapse behavior for submenus and can be customized for different use cases.
      </p>
      <p>
        To ensure proper styling and functionality, you must use the <code>renderLink</code> prop to define how links are rendered. The <code>navbar-item-base</code> class is essential for consistent styling, including the default gray font color and active state styling.
      </p>
      <UQSpacer size="m" />

      {/* Basic Navbar */}
      <h2>Basic Navbar</h2>
      <p>This is a simple navbar with a few links and icons.</p>
      <UQSpacer size="s" />
      <div style={{ border: '1px solid #ccc', padding: '1rem' }}>
        <UQNavbar
          links={[
            { text: 'Home', url: '/', icon: 'home-01' },
            { text: 'About', url: '/about', icon: 'info' },
            { text: 'Contact', url: '/contact', icon: 'mail' },
          ]}
          logoLight={svgLogoBase64}
          logoDark={svgLogoBase64}
          location={window.location}
          renderLink={(link, children) => (
            <Link to={link.url} className="navbar-item-base">
              {children}
            </Link>
          )}
        />
      </div>
      <UQSpacer size="s" />
      <UQCode code={basicNavbarCode} enableCopy />
      <UQSpacer size="m" />

      {/* Collapsible Navbar */}
      <h2>Collapsible Navbar</h2>
      <p>This navbar includes collapsible submenus for hierarchical navigation.</p>
      <UQSpacer size="s" />
      <div style={{ border: '1px solid #ccc', padding: '1rem' }}>
        <UQNavbar
          links={[
            {
              text: 'Services',
              url: '/services',
              icon: 'settings',
              subLevel: [
                { text: 'Consulting', url: '/services/consulting' },
                { text: 'Development', url: '/services/development' },
              ],
            },
            { text: 'Portfolio', url: '/portfolio', icon: 'folder' },
          ]}
          singleCollapse={true}
          location={window.location}
          renderLink={(link, children) => (
            <Link to={link.url} className="navbar-item-base">
              {children}
            </Link>
          )}
        />
      </div>
      <UQSpacer size="s" />
      <UQCode code={collapsibleNavbarCode} enableCopy />
      <UQSpacer size="m" />

      {/* Mini Navbar */}
      <h2>Mini Navbar</h2>
      <p>This is a compact version of the navbar displayed inside a <code>div</code>.</p>
      <UQSpacer size="s" />
      <div style={{ width: '300px', border: '1px solid #ccc', padding: '1rem' }}>
        <UQNavbar
          links={[
            { text: 'Dashboard', url: '/dashboard', icon: 'airplay' },
            { text: 'Reports', url: '/reports', icon: 'alert-square' },
          ]}
          logoLight={svgLogoBase64}
          logoDark={svgLogoBase64}
          location={window.location}
          renderLink={(link, children) => (
            <Link to={link.url} className="navbar-item-base">
              {children}
            </Link>
          )}
        />
      </div>
      <UQSpacer size="s" />
      <UQCode code={miniNavbarCode} enableCopy />
      <UQSpacer size="m" />

      {/* Horizontal Collapsed Navbar */}
      <h2>Horizontal Collapsed Navbar</h2>
      <p>This navbar is collapsed horizontally showing only icons with tooltips. Items with sub-menus will show dropdown menus when clicked. Perfect for space-constrained layouts while maintaining navigation functionality.</p>
      <UQSpacer size="s" />
      
      
      <div style={{ display: 'flex', height: '400px', border: '1px solid #ccc' }}>
        <UQNavbar
          links={[
            { text: 'Dashboard', url: '/dashboard', icon: 'home-01' },
            { 
              text: 'Analytics', 
              url: '/analytics', 
              icon: 'bar-chart',
              subLevel: [
                { text: 'Reports', url: '/analytics/reports', icon: 'file-text' },
                { text: 'Charts', url: '/analytics/charts', icon: 'bar-chart' },
                { text: 'Data Export', url: '/analytics/export', icon: 'download' },
              ]
            },
            { 
              text: 'Settings', 
              url: '/settings', 
              icon: 'settings',
              subLevel: [
                { text: 'General', url: '/settings/general', icon: 'settings' },
                { text: 'Security', url: '/settings/security', icon: 'shield' },
                { text: 'Integrations', url: '/settings/integrations', icon: 'link' },
              ]
            },
            { text: 'Profile', url: '/profile', icon: 'user' },
          ]}
          logoLight={svgLogoBase64}
          logoDark={svgLogoBase64}
          logoLightSmall={svgSmallLogoBase64}
          logoDarkSmall={svgSmallLogoBase64}
          horizontalCollapsed={true}
          location={window.location}
          renderLink={(link, children) => (
            <Link to={link.url} className="navbar-item-base">
              {children}
            </Link>
          )}
          content={
            <div style={{ flex: 1, padding: '1rem', background: 'var(--bg-color, #f8f9fa)', color: 'var(--text-color, #212529)' }}>
              <h3>Main Content Area</h3>
              <p>This demonstrates how the collapsed navbar takes minimal space while keeping the main content area spacious. Click on Analytics or Settings to see the dropdown menus.</p>
            </div>
          }
        />
      </div>
      <UQSpacer size="s" />
      <UQCode code={horizontalCollapsedNavbarCode} enableCopy />
      <UQSpacer size="m" />

      {/* Responsive UNQ Navbar */}
      <h2>Responsive UNQ Navbar (Mobile Collapsed / Desktop Normal)</h2>
      <p>This example demonstrates a responsive navbar that automatically adapts to screen size. On mobile (&lt;768px), it uses horizontally collapsed layout for space efficiency on small screens. On desktop (≥768px), it switches to normal layout for full navigation visibility. Try resizing your browser window to see the responsive behavior.</p>
      <UQSpacer size="s" />
      <ResponsiveUNQNavbarExample />
      <UQSpacer size="s" />
      <UQCode code={responsiveUNQNavbarCode} enableCopy />
      <UQSpacer size="m" />

      {/* Real UNQ Logo with Subitems */}
      <h2>UNQ Navbar with Real Logo and Subitems (Horizontal Collapsed)</h2>
      <p>This example demonstrates the navbar using the actual UNQ logos with a comprehensive navigation structure including nested subitems. It showcases the full functionality with multi-level dropdowns in a horizontal collapsed layout that saves space while maintaining full functionality.</p>
      <UQSpacer size="s" />
      <div style={{ border: '1px solid #ccc', padding: '1rem' }}>
        <UQNavbar
          links={[
            { text: 'Dashboard', url: '/', icon: 'home-01' },
            { 
              text: 'Projects', 
              url: '/projects', 
              icon: 'folder',
              subLevel: [
                { text: 'Active Projects', url: '/projects/active', icon: 'activity' },
                { text: 'Completed Projects', url: '/projects/completed', icon: 'check-circle' },
                { text: 'Archived Projects', url: '/projects/archived', icon: 'archive' },
                { 
                  text: 'Templates', 
                  url: '/projects/templates', 
                  icon: 'template',
                  subLevel: [
                    { text: 'Web Templates', url: '/projects/templates/web', icon: 'globe' },
                    { text: 'Mobile Templates', url: '/projects/templates/mobile', icon: 'smartphone' },
                    { text: 'API Templates', url: '/projects/templates/api', icon: 'code' }
                  ]
                }
              ]
            },
            {
              text: 'Analytics',
              url: '/analytics',
              icon: 'activity',
              subLevel: [
                { text: 'Performance', url: '/analytics/performance', icon: 'trending-up' },
                { text: 'User Insights', url: '/analytics/users', icon: 'users' },
                { text: 'Revenue Reports', url: '/analytics/revenue', icon: 'dollar-sign' },
                {
                  text: 'Custom Reports',
                  url: '/analytics/custom',
                  icon: 'bar-chart',
                  subLevel: [
                    { text: 'Create Report', url: '/analytics/custom/create', icon: 'plus' },
                    { text: 'Saved Reports', url: '/analytics/custom/saved', icon: 'bookmark' },
                    { text: 'Scheduled Reports', url: '/analytics/custom/scheduled', icon: 'clock' }
                  ]
                }
              ]
            },
            {
              text: 'Team',
              url: '/team',
              icon: 'users',
              subLevel: [
                { text: 'Members', url: '/team/members', icon: 'user' },
                { text: 'Roles & Permissions', url: '/team/roles', icon: 'shield' },
                { text: 'Departments', url: '/team/departments', icon: 'briefcase' },
                {
                  text: 'HR Management',
                  url: '/team/hr',
                  icon: 'user-plus',
                  subLevel: [
                    { text: 'Onboarding', url: '/team/hr/onboarding', icon: 'user-check' },
                    { text: 'Performance Reviews', url: '/team/hr/reviews', icon: 'star' },
                    { text: 'Time Tracking', url: '/team/hr/time', icon: 'clock' }
                  ]
                }
              ]
            },
            {
              text: 'Settings',
              url: '/settings',
              icon: 'settings',
              subLevel: [
                { text: 'General', url: '/settings/general', icon: 'sliders' },
                { text: 'Security', url: '/settings/security', icon: 'lock' },
                { text: 'Notifications', url: '/settings/notifications', icon: 'bell' },
                { text: 'Billing', url: '/settings/billing', icon: 'credit-card' },
                {
                  text: 'Integrations',
                  url: '/settings/integrations',
                  icon: 'link',
                  subLevel: [
                    { text: 'API Keys', url: '/settings/integrations/api', icon: 'key' },
                    { text: 'Webhooks', url: '/settings/integrations/webhooks', icon: 'zap' },
                    { text: 'Third-party Apps', url: '/settings/integrations/apps', icon: 'grid' }
                  ]
                }
              ]
            },
            { text: 'Help', url: '/help', icon: 'help-circle' }
          ]}
          logoLight={logoLight}
          logoDark={logoDark}
          logoLightSmall={logoLightSmall}
          logoDarkSmall={logoDarkSmall}
          horizontalCollapsed={true}
          location={window.location}
          renderLink={(link, children) => (
            <Link to={link.url} className="navbar-item-base">
              {children}
            </Link>
          )}
          content={
            <div style={{ flex: 1, padding: '2rem', background: 'var(--bg-color, #f8f9fa)', color: 'var(--text-color, #212529)' }}>
              <h3>UNQ Consulting Platform</h3>
              <p>Welcome to the UNQ Consulting platform. This horizontally collapsed navbar demonstrates the full navigation structure with multi-level subitems. The compact sidebar layout maximizes content space while keeping all navigation features accessible. Try hovering over the navigation icons to see the dropdown menus with nested navigation.</p>
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--card-bg, #ffffff)', borderRadius: '8px', border: '1px solid var(--border-color, #e9ecef)' }}>
                <h4>Navigation Features:</h4>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li>Horizontal collapsed layout for space efficiency</li>
                  <li>Multi-level dropdown navigation</li>
                  <li>Icon-based visual hierarchy</li>
                  <li>Responsive design with collapsible states</li>
                  <li>Real UNQ branding integration</li>
                  <li>Contextual content organization</li>
                </ul>
              </div>
            </div>
          }
        />
      </div>
      <UQSpacer size="s" />
      <UQCode code={unqNavbarCode} enableCopy />
      <UQSpacer size="m" />

      {/* Toggle Horizontal Collapse Navbar */}
      <h2>Toggle Horizontal Collapse Navbar</h2>
      <p>This example demonstrates how to dynamically toggle between normal and horizontal collapsed modes. Use the toggle button to see the navbar expand and collapse.</p>
      <UQSpacer size="s" />
      <ToggleCollapseExample />
      <UQSpacer size="m" />

      {/* Small Logos Navbar */}
      <h2>Navbar with Small Logos</h2>
      <p>This navbar demonstrates the use of separate small logos for different states or themes. The <code>logoLightSmall</code> and <code>logoDarkSmall</code> props allow you to specify optimized logos for compact displays.</p>
      <UQSpacer size="s" />
      <div style={{ border: '1px solid #ccc', padding: '1rem' }}>
        <UQNavbar
          links={[
            { text: 'Home', url: '/', icon: 'home-01' },
            { text: 'About', url: '/about', icon: 'info' },
            { text: 'Contact', url: '/contact', icon: 'mail' },
          ]}
          logoLight={svgLogoBase64}
          logoDark={svgLogoBase64}
          logoLightSmall={svgSmallLogoBase64}
          logoDarkSmall={svgSmallLogoBase64}
          location={window.location}
          renderLink={(link, children) => (
            <Link to={link.url} className="navbar-item-base">
              {children}
            </Link>
          )}
        />
      </div>
      <UQSpacer size="s" />
      <UQCode code={smallLogosNavbarCode} enableCopy />
    </>
  );
};

export default NavbarPage;